"use strict";

import PointerInteraction from 'ol/interaction/Pointer';

/**
 * @classdesc
 * Kartenaktionen zum Selektieren von Features auf Basis von OpenLayers.
 *
 * @class
 * @extends gc.gis.map.GCMapSelectInteraction
 * @memberof gc.gis.map
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCMapView} map Karte, der die Interaktion gehört
 * @param {Object} [opt_options] optionale Aufrufoptionen
 */
gc.gis.map.OLMapSelectInteraction = function(map, opt_options)
{
	gc.gis.map.GCMapSelectInteraction.call(this, map, opt_options);

	/**
	 * Style für hover Feature.
	 * @type {ol.style.Style|null}
	 * @private
	 */
	this._hoverStyle = opt_options.hoverStyle ? opt_options.hoverStyle : null;

	/**
	 * @type {ol.Map}
	 * @private
	 */
	this._ol_map = this.map.getMap().getImpl();

	/**
	 * Layer, die zur Selektion herangezogen werden sollen,
	 * @type {ol.layer.Vector[]}
	 * @private
	 */
	this._ol_layers = [];

	/**
	 * Datenquelle zum Laden von Vektordaten
	 * @type {gc.gis.source.OLMapCatalogueVector}
	 */
	this._sourceMapCatalouge = new gc.gis.source.OLMapCatalogueVector(this.map, {
		sourceLayers: [],    // leere Liste zunächst keine Abfrage
		forceMapProjection: false,
		returnWritableOnly: false,
		ignoreSupportFeatureList: false,
		loadMessage: 'Lade Vektordaten für Auswahl ...'
	});

	/**
	 * Tracking-Layer welcher die auswählbaren Geometrien aus dem Kartenkatalog enthält enthält.
	 * @type {gc.gis.layer.OLTrackingLayer}
	 * @private
	 */
	this._layerMapCatalogue = new gc.gis.layer.OLTrackingLayer(this.map.getMap(), {});
	this._layerMapCatalogue.setStyle(gc.gis.iface.styleFactory.getStyle(gc.gis.factory.StyleType.TRANSPARENT));
	this._layerMapCatalogue.getImpl().setSource(this._sourceMapCatalouge.getSource());

	/**
	 * @type {ol.interaction.Pointer}
	 * @private
	 */
	this._pointerInteraction = null;

	/**
	 * Merker für Cursordarstellung.
	 * @type {string|null}
	 * @private
	 */
	this._prevCursor = null;

	/**
	 * Merker für Feature-Hover.
	 * @type {ol.Feature|null}
	 * @private
	 */
	this._prevFeature = null;

	this._createInteraction();
	this._initEvents();
};
gc.gis.map.OLMapSelectInteraction.prototype = Object.create(gc.gis.map.GCMapSelectInteraction.prototype);

/**
 * @inheritdoc
 */
gc.gis.map.OLMapSelectInteraction.prototype.setActive = function(state)
{
	state ? this._activate() : this._deactivate();
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapSelectInteraction.prototype.isActive = function()
{
	return this._pointerInteraction.getActive() === true;
};

/**
 * @inheritDoc
 */
gc.gis.map.OLMapSelectInteraction.prototype.setLayers = function (layers)
{
	const internalLayers = [];
	let i,ii;

	this._ol_layers = [];
	for (i = 0, ii = layers.length; i < ii; i++)
	{
		if (layers[i] instanceof gc.gis.layer.GCVectorLayer)
		{
			this._ol_layers.push(layers[i]);
		}
		else if (layers[i] instanceof gc.gis.layer.GCLayer && layers[i].isInternal())
		{
			internalLayers.push(layers[i]);
		}
	}

	if (internalLayers.length > 0)
	{
		this._ol_layers.push(this._layerMapCatalogue.getImpl());

		this._sourceMapCatalouge.setSourceLayers(internalLayers);
		this._sourceMapCatalouge.refresh();
	}
};

/**
 * Initialisiert die notwendigen Interaktionen für die Geometrie-Bearbeitungsfunktion.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._createInteraction = function()
{
	const self = this;
	let props;

	// Reihenfolge, erst PointerInteraction, dann Modify ist für weitere Verarbeitung insbesondere Reihenfolge Event-Verarbeitung wichtig
	if (!this._pointerInteraction)
	{
		props = {
			handleDownEvent: function (evt) {
				return self._doPointerDown(evt);
			},
			handleMoveEvent: function (evt) {
				return self._doPointerMove(evt);
			}
		};

		this._pointerInteraction = new PointerInteraction(props);
		this._pointerInteraction.set('_is_gc', false, true);
		this._ol_map.addInteraction(this._pointerInteraction);

		this._pointerInteraction.setActive(false);
	}
};

/**
 * Initialisiert die notwendigen Events für die Objektauswahl.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._initEvents = function()
{
	this.map.on(gc.gis.map.GCMapEvent.MOUSE_LEAVE, this, function (evt) {
		this._doPointerLeaveMap(evt);
	});
};

/**
 * Aktiviert die Interaktion.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._activate = function()
{
	if (!this.isActive())
	{
		this._pointerInteraction.setActive(true);
		this.map.getMap().addTrackingLayer(this._layerMapCatalogue);
	}
};

/**
 * Deaktiviert die Interaktion.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._deactivate = function()
{
	if (this.isActive())
	{
		this._pointerInteraction.setActive(false);
		this.map.getMap().removeTrackingLayer(this._layerMapCatalogue);
	}
};

/**
 * Liefert ein Feature anhand einer Pixel-Koordinate.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._getFeatureAtPixel = function (pixel)
{
	const self = this;

	// forEach endet, sobald ein truthy Wert zurückgegeben wird
	return this._ol_map.forEachFeatureAtPixel(pixel,
		function (feature, layer) {
			if (self._ol_layers.indexOf(layer) > -1)
			{
				return {feature: feature, layer: layer};
			}
		}) || null;
};

/**
 * @private
 * @method
 * @return {boolean} `true` to start the drag sequence.
 */
gc.gis.map.OLMapSelectInteraction.prototype._doPointerDown = function (evt)
{
	const result = this._getFeatureAtPixel(evt.pixel);
	if (result)
	{
		this._fireEvent(
			gc.gis.map.GCMapSelectInteractionEvent.CLICK,
			{
				feature: new gc.gis.feature.OLFeature(result.feature)
			}
		);
	}

	return false;
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._doPointerMove = function (evt)
{
	const target = this._ol_map.getTargetElement(),
		result = this._getFeatureAtPixel(evt.pixel);

	if (result)
	{
		if (this._prevCursor === null)
		{
			this._prevCursor = target.style.cursor;
		}
		target.style.cursor = 'pointer';

		if (result.feature !== this._prevFeature)
		{
			if (this._prevFeature)
			{
				this._fireFeatureEnterLeaveEvent(this._prevFeature, true);
				this._prevFeature.setStyle(null);
			}

			this._prevFeature = result.feature;
			this._fireFeatureEnterLeaveEvent(this._prevFeature, false);
			if (this._hoverStyle)
			{
				result.feature.setStyle(this._hoverStyle);
			}
		}
	}
	else
	{
		if (this._prevCursor !== null)
		{
			target.style.cursor = this._prevCursor;
		}
		this._prevCursor = null;

		if (this._prevFeature !== null)
		{
			this._fireFeatureEnterLeaveEvent(this._prevFeature, true);
			this._prevFeature.setStyle(null);
		}
		this._prevFeature = null;
	}
};

/**
 * @private
 * @method
 */
gc.gis.map.OLMapSelectInteraction.prototype._doPointerLeaveMap = function (evt)
{
	const target = this._ol_map.getTargetElement();

	if (this._prevCursor !== null)
	{
		target.style.cursor = this._prevCursor;
	}
	this._prevCursor = null;

	if (this._prevFeature !== null)
	{
		this._fireFeatureEnterLeaveEvent(this._prevFeature, true);
		this._prevFeature.setStyle(null);
	}
	this._prevFeature = null;
};

/**
 * Löst eine Ereignisbehandlungsroutine aus die anzeigt, das ein Feature mit der Maus betreten oder verlassen wurde.
 *
 * @private
 * @method
 * @param {ol.Feature} feature
 * @param {boolean} leave
 */
gc.gis.map.OLMapSelectInteraction.prototype._fireFeatureEnterLeaveEvent = function (feature, leave)
{
	this._fireEvent(
		leave ? gc.gis.map.GCMapSelectInteractionEvent.LEAVE : gc.gis.map.GCMapSelectInteractionEvent.ENTER,
		{
			feature: new gc.gis.feature.OLFeature(feature)
		}
	);
};
